import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, FlaskConical, Trophy, LineChart, BookOpen, Wand2, Crown, ArrowRight, Star, Quote, Check } from "lucide-react";

const HERO_IMAGE = "https://images.unsplash.com/photo-1707409066859-a90674383d19?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxkaXZlcnNlJTIwd29tZW4lMjBza2luY2FyZSUyMHBvcnRyYWl0fGVufDB8fHx8MTc3OTAxNjcyN3ww&ixlib=rb-4.1.0&q=85";

const BENEFITS = [
  { title: "Personalized Routines", desc: "AM + PM rituals built for your exact skin type and hair texture.", icon: Sparkles },
  { title: "Dupe Finder", desc: "Find affordable swaps for any product — matched to your profile.", icon: Wand2 },
  { title: "DIY Beauty Zone", desc: "Studio-grade recipes you can mix in your kitchen.", icon: FlaskConical },
  { title: "Daily Challenges", desc: "7, 14, 21, 30-day glow sprints with collectible badges.", icon: Trophy },
  { title: "Progress Tracking", desc: "Photo journals + your weekly Glow Score.", icon: LineChart },
  { title: "Your Glow Report", desc: "Daily insights from a dermatologist & trichologist.", icon: BookOpen },
];

const TESTIMONIALS = [
  {
    name: "Nicole, California",
    tone: "#F4D7C1",
    quote: "I always struggled to find skincare advice that actually worked for my sensitive skin without causing redness. Glow & Grow finally gets it right.",
  },
  {
    name: "Imani, Atlanta",
    tone: "#82491A",
    quote: "The 4C hair routines actually understand my texture. My locs have never been this healthy.",
  },
  {
    name: "Sofia, São Paulo",
    tone: "#C08050",
    quote: "The Dupe Finder saved me $200 in a month. It's like having a chemist in my pocket.",
  },
];

export default function Landing() {
  const navigate = useNavigate();
  return (
    <div className="w-full min-h-screen bg-cream" data-testid="landing-page">
      {/* Top bar */}
      <header className="px-6 py-5 flex items-center justify-between max-w-6xl mx-auto">
        <div className="flex items-center gap-2.5">
          <img
            src="/brand/logo.png"
            alt="Glow & Grow logo"
            className="w-10 h-10 rounded-full object-cover"
            data-testid="brand-logo"
          />
          <span className="text-2xl font-serif text-cocoa">Glow &amp; Grow</span>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/login" data-testid="header-login" className="text-sm text-cocoa/70 hover:text-cocoa">
            Log in
          </Link>
          <button
            onClick={() => navigate("/signup")}
            data-testid="header-signup"
            className="rounded-full bg-rose hover:bg-rose-dark text-white px-5 py-2 text-sm font-medium transition-all"
          >
            Get started
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="px-6 max-w-6xl mx-auto pt-6 pb-20 md:grid md:grid-cols-2 md:gap-12 md:items-center">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose/10 text-rose text-xs uppercase tracking-[0.18em] font-medium mb-5">
            <Sparkles size={11} />
            Your luxury beauty self-growth OS
          </div>
          <h1 className="font-serif text-[2.6rem] leading-[1.05] md:text-[3.4rem] lg:text-[4rem] text-cocoa mb-5 text-balance">
            Glow Smarter.
            <br />
            Grow Stronger.
            <br />
            <span className="italic text-gold-gradient">Become Her.</span>
          </h1>
          <p className="text-cocoa/70 text-lg font-light max-w-md mb-7 leading-relaxed">
            Personalized skincare, haircare, powerful beauty tools, and confidence-building — in one luxury beauty growth system.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => navigate("/signup?next=%2Fapp%2Fpremium")}
              data-testid="hero-trial-cta"
              className="inline-flex items-center justify-center gap-2 rounded-full text-white px-7 py-3.5 font-medium transition-all shadow-rose"
              style={{ background: "linear-gradient(135deg, #C9975A 0%, #E3BA85 100%)" }}
            >
              <Crown size={16} />
              Start 7-Day Free Trial
            </button>
            <button
              onClick={() => navigate("/signup")}
              data-testid="hero-cta"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-rose hover:bg-rose-dark text-white px-7 py-3.5 font-medium transition-all shadow-rose"
            >
              Start My Glow Journey
              <ArrowRight size={18} />
            </button>
          </div>
          <div className="mt-7 flex items-center gap-4 text-xs text-cocoa/55">
            <div className="flex -space-x-2">
              <div className="w-7 h-7 rounded-full border-2 border-cream" style={{ background: "#5C3018" }} />
              <div className="w-7 h-7 rounded-full border-2 border-cream" style={{ background: "#82491A" }} />
              <div className="w-7 h-7 rounded-full border-2 border-cream" style={{ background: "#C08050" }} />
              <div className="w-7 h-7 rounded-full border-2 border-cream" style={{ background: "#E8C09A" }} />
            </div>
            <span className="font-light">Built for every skin tone &amp; hair texture.</span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="relative mt-12 md:mt-0"
        >
          <div className="relative rounded-[28px] overflow-hidden shadow-floating">
            <img src={HERO_IMAGE} alt="Diverse women glowing" className="w-full h-[460px] md:h-[560px] object-cover" />
            <div className="absolute bottom-5 left-5 right-5 bg-white/90 backdrop-blur-md rounded-2xl p-4 flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center"
                style={{ background: "linear-gradient(135deg, #C9975A 0%, #E3BA85 100%)" }}
              >
                <Crown size={16} className="text-white" />
              </div>
              <div className="text-xs">
                <p className="text-cocoa/55 uppercase tracking-wider mb-0.5">Today's affirmation</p>
                <p className="font-serif text-cocoa text-base leading-tight italic">
                  "You are radiant in every shade."
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Benefits */}
      <section id="features" className="px-6 max-w-6xl mx-auto py-16">
        <div className="text-center mb-10">
          <p className="text-xs uppercase tracking-[0.2em] text-gold font-medium mb-3">Inside the app</p>
          <h2 className="font-serif text-4xl md:text-5xl text-cocoa">A complete beauty system</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {BENEFITS.map(({ title, desc, icon: Icon }, i) => (
            <motion.div
              key={title}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              className="bg-white rounded-[20px] p-6 shadow-card"
            >
              <div className="w-11 h-11 rounded-full bg-rose/10 flex items-center justify-center mb-4">
                <Icon size={20} className="text-rose" strokeWidth={1.6} />
              </div>
              <h3 className="font-serif text-2xl text-cocoa mb-2">{title}</h3>
              <p className="text-sm text-cocoa/65 font-light leading-relaxed">{desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="px-6 max-w-6xl mx-auto py-16">
        <div className="text-center mb-10">
          <p className="text-xs uppercase tracking-[0.2em] text-gold font-medium mb-3">How it works</p>
          <h2 className="font-serif text-4xl md:text-5xl text-cocoa">Your glow in three steps</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { n: "01", title: "Sign up", desc: "Free instant signup — no email confirmation." },
            { n: "02", title: "Complete the quiz", desc: "Six gentle questions about your skin, hair, and goals." },
            { n: "03", title: "Get your plan", desc: "A personalized glow ritual ready every morning." },
          ].map(({ n, title, desc }) => (
            <div key={n} className="bg-white rounded-[20px] p-7 shadow-card">
              <p className="font-serif text-5xl text-gold-gradient font-light mb-3">{n}</p>
              <h3 className="font-serif text-2xl text-cocoa mb-2">{title}</h3>
              <p className="text-sm text-cocoa/65 font-light">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonials */}
      <section className="px-6 max-w-6xl mx-auto py-16">
        <div className="text-center mb-10">
          <p className="text-xs uppercase tracking-[0.2em] text-gold font-medium mb-3">Stories</p>
          <h2 className="font-serif text-4xl md:text-5xl text-cocoa">Glowing women, real results</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {TESTIMONIALS.map((t) => (
            <div key={t.name} className="bg-white rounded-[20px] p-6 shadow-card relative">
              <Quote size={22} className="text-gold mb-3 opacity-60" />
              <p className="text-cocoa text-base font-light leading-relaxed italic mb-5">"{t.quote}"</p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full" style={{ background: t.tone }} />
                <div>
                  <p className="text-sm font-medium text-cocoa">{t.name}</p>
                  <div className="flex gap-0.5 text-gold">
                    {[...Array(5)].map((_, i) => <Star key={i} size={11} fill="currentColor" />)}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Premium teaser */}
      {/* ============================================================
          PRICING
          ============================================================ */}
      <section id="pricing" className="px-6 max-w-6xl mx-auto py-16" data-testid="pricing-section">
        <div className="text-center mb-10 max-w-2xl mx-auto">
          <p className="text-xs uppercase tracking-[0.22em] text-gold font-medium mb-3">Pricing</p>
          <h2 className="font-serif text-4xl md:text-5xl text-cocoa mb-4 leading-tight">
            Start your beauty evolution
          </h2>
          <p className="text-cocoa/65 font-light text-lg">
            Free to explore, more to unlock.
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2 max-w-4xl mx-auto">
          {/* FREE */}
          <div
            className="rounded-[28px] bg-white shadow-card p-8 flex flex-col relative"
            data-testid="pricing-free-card"
          >
            <p className="text-xs uppercase tracking-[0.2em] text-cocoa/50 font-medium mb-3">Free</p>
            <div className="mb-6">
              <span className="font-serif text-5xl text-cocoa">$0</span>
              <span className="text-cocoa/50 font-light ml-1">/forever</span>
            </div>
            <ul className="space-y-3 mb-8 flex-1">
              {[
                "AM/PM routine basics",
                "Progress photo log",
                "DIY Beauty Zone",
                "Glow Challenges",
                "Full Expert Library access",
              ].map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm font-light text-cocoa/85">
                  <span className="mt-1 w-4 h-4 rounded-full bg-rose/10 flex items-center justify-center shrink-0">
                    <Check size={10} className="text-rose" strokeWidth={2.6} />
                  </span>
                  {f}
                </li>
              ))}
            </ul>
            <button
              onClick={() => navigate("/signup")}
              data-testid="pricing-free-cta"
              className="rounded-full border border-cocoa/15 py-3.5 font-medium text-sm text-cocoa hover:bg-cream transition-all"
            >
              Start free
            </button>
          </div>

          {/* PREMIUM */}
          <div
            className="rounded-[28px] p-8 flex flex-col relative overflow-hidden shadow-floating"
            style={{ background: "linear-gradient(155deg, #FFF7EB 0%, #FDECEF 55%, #F8E1D5 100%)" }}
            data-testid="pricing-premium-card"
          >
            <div
              className="absolute top-6 right-6 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.2em] font-medium text-white shadow-rose"
              style={{ background: "linear-gradient(135deg, #C9975A 0%, #E3BA85 100%)" }}
            >
              <Crown size={11} /> Most loved
            </div>
            <p className="text-xs uppercase tracking-[0.2em] text-gold font-medium mb-3">Glow Premium</p>
            <div className="mb-1">
              <span className="font-serif text-5xl text-cocoa">$12</span>
              <span className="text-cocoa/60 font-light ml-1">/month</span>
            </div>
            <p className="text-sm text-cocoa/60 font-light mb-6">
              or <span className="font-medium text-cocoa">$89/year</span> — save ~38%
            </p>
            <ul className="space-y-3 mb-8 flex-1">
              {[
                "Everything in Free, plus:",
                "Daily Insights from your Glow Report",
                "Adaptive weekly routines",
                "Dupe Finder for any beauty product",
                "Premium DIY recipes, tailored to your profile",
                "Progress trends & Glow Score",
              ].map((f, i) => (
                <li
                  key={f}
                  className={`flex items-start gap-3 text-sm ${
                    i === 0 ? "font-medium text-cocoa" : "font-light text-cocoa/85"
                  }`}
                >
                  <span className="mt-1 w-4 h-4 rounded-full bg-rose/15 flex items-center justify-center shrink-0">
                    <Check size={10} className="text-rose" strokeWidth={2.6} />
                  </span>
                  {f}
                </li>
              ))}
            </ul>
            <button
              onClick={() => navigate("/signup")}
              data-testid="pricing-premium-cta"
              className="rounded-full py-3.5 font-medium text-sm text-white transition-all shadow-rose"
              style={{ background: "linear-gradient(135deg, #C9748A 0%, #B8657A 100%)" }}
            >
              Start Free — Upgrade to Premium
            </button>
            <p className="text-[11px] text-cocoa/70 text-center mt-3 font-medium">
              Cancel anytime.
            </p>
          </div>
        </div>
      </section>


      <section className="px-6 max-w-6xl mx-auto py-16">
        <div
          className="rounded-[28px] p-8 md:p-14 shadow-floating overflow-hidden relative text-center"
          style={{ background: "linear-gradient(135deg, #C9748A 0%, #B8657A 100%)" }}
        >
          {/* Decorative sparkle accents */}
          <span aria-hidden className="absolute -top-6 -right-6 text-white/15 select-none pointer-events-none">
            <Sparkles size={140} strokeWidth={1} />
          </span>
          <span aria-hidden className="absolute -bottom-8 -left-8 text-white/10 select-none pointer-events-none">
            <Sparkles size={180} strokeWidth={1} />
          </span>
          <div className="relative z-10 max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-5 bg-white/15 backdrop-blur-sm">
              <Crown size={12} className="text-white" />
              <span className="text-xs uppercase tracking-[0.18em] font-medium text-white">Glow Premium</span>
            </div>
            <h2 className="font-serif text-4xl md:text-5xl text-white mb-4 text-balance">
              Become the most cared-for version of you.
            </h2>
            <p className="text-white/85 font-light mb-7 max-w-xl mx-auto">
              Adaptive routines, daily expert insights, dupe finder, premium DIY rituals &amp; glow challenges.
            </p>
            <button
              onClick={() => navigate("/signup")}
              data-testid="premium-teaser-cta"
              className="rounded-full px-7 py-3.5 font-medium text-cocoa bg-white hover:bg-cream transition-all inline-flex items-center gap-2"
            >
              Start free — upgrade anytime
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </section>

      <footer className="mt-16 bg-white border-t border-border" data-testid="landing-footer">
        <div className="max-w-6xl mx-auto px-6 py-12">
          <div className="grid gap-10 md:grid-cols-[1.4fr_1fr_1fr_1fr] mb-10">
            <div>
              <div className="flex items-center gap-2.5 mb-4">
                <img src="/brand/logo.png" alt="Glow & Grow logo" className="w-10 h-10 rounded-full object-cover" />
                <span className="text-2xl font-serif text-cocoa">Glow &amp; Grow</span>
              </div>
              <p className="font-serif italic text-cocoa/70 text-lg max-w-sm leading-snug">
                Built for every woman, every shade.
              </p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.18em] text-gold font-medium mb-4">Explore</p>
              <ul className="space-y-2.5 text-sm font-light">
                <li><a href="#top" data-testid="footer-home" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="text-cocoa/75 hover:text-rose">Home</a></li>
                <li><a href="#features" data-testid="footer-features" className="text-cocoa/75 hover:text-rose">Features</a></li>
                <li><a href="#pricing" data-testid="footer-pricing" className="text-cocoa/75 hover:text-rose">Pricing</a></li>
              </ul>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.18em] text-gold font-medium mb-4">Account</p>
              <ul className="space-y-2.5 text-sm font-light">
                <li><Link to="/login" data-testid="footer-login" className="text-cocoa/75 hover:text-rose">Log in</Link></li>
                <li><Link to="/signup" data-testid="footer-signup" className="text-cocoa/75 hover:text-rose">Sign up</Link></li>
                <li><Link to="/contact" data-testid="footer-contact" className="text-cocoa/75 hover:text-rose">Contact</Link></li>
              </ul>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.18em] text-gold font-medium mb-4">Legal</p>
              <ul className="space-y-2.5 text-sm font-light">
                <li><Link to="/terms" data-testid="footer-terms" className="text-cocoa/75 hover:text-rose">Terms of Service</Link></li>
                <li><Link to="/privacy" data-testid="footer-privacy" className="text-cocoa/75 hover:text-rose">Privacy Policy</Link></li>
                <li><Link to="/refund" data-testid="footer-refund" className="text-cocoa/75 hover:text-rose">Refund Policy</Link></li>
              </ul>
            </div>
          </div>
          <div className="pt-6 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-sm text-cocoa/55 font-light">© {new Date().getFullYear()} Glow &amp; Grow. All rights reserved.</p>
            <p className="text-xs text-cocoa/45 font-light tracking-wide">Beauty meets technology.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
